import {
    AlertColor,
    Autocomplete,
    Button,
    CircularProgress,
    MenuItem,
    Modal,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle } from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DropDownComboOption } from "../../component/GenericFilter";
import { EndUserListItem } from "./EndUserAccessories";
import { renewToken } from "../../../services/AuthService";
import { OpenMode } from "../../../common/Enums";
import { GeneralDialog } from "../../component/GeneralDialog";
import { CommonProps } from "../../component/MainContents";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    })
);

export interface EndUserSingleHandle {
    Initialise: (mode: OpenMode, endUser: EndUserListItem) => void;
}

export const EndUserSingle = React.forwardRef(
    (props: CommonProps, ref: React.Ref<EndUserSingleHandle>) => {
        const style = { ...useStyles(), ...globalTheme() };

        const tinyScreen = useMediaQuery('(max-width:1366px)');
        const smallScreen = useMediaQuery('(max-width:1920px)');
        const normalScreen = useMediaQuery('(max-width:2560px)');
    
        const [open, setOpen] = React.useState(false);
        const [openMode, setOpenMode] = React.useState<OpenMode>(OpenMode.NEW);
        const [loading, setLoading] = React.useState<boolean>(false);
        const [hasError, setHasError] = React.useState(false);
        const [endUser, setEndUser] = React.useState<EndUserListItem>();
        const [originalEndUser, setOriginalEndUser] = React.useState<EndUserListItem>();
        const [saved, setSaved] = React.useState<boolean>(false);
        const [selectedCustomer, setSelectedCustomer] = React.useState<DropDownComboOption>();
        const [regionsWithBlank, setRegionsWithBlank] = React.useState<DropDownComboOption[]>([]);

        //Dialogs and snacks
        const hasChanges = React.useRef<boolean>(false);
        const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
        const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
            open: false,
            message: "",
            subMessage: "",
            severity: undefined,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

        function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
            setSnackSettings({
                open: true,
                message: message,
                subMessage: subMessage,
                severity: colour,
                onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
            });
        }

        useImperativeHandle(ref, () => ({
            Initialise(mode: OpenMode, endUser: EndUserListItem) {
                setOpen(true);
                setSaved(false);
                renewToken();
        
                let tempRegions = [...props.regions];
                tempRegions.unshift({name: "", value: "-1"});
                setRegionsWithBlank(tempRegions);
                hasChanges.current = false;
                setOpenMode(mode);
                if (endUser)
                {
                    let customer = props.customers.find(x => x.name == endUser.customerName);
                    if (!customer)
                    {
                        customer = props.customers.find(x => x.value == endUser.customerID?.toString());
                    }
                    setSelectedCustomer(customer);
                    setEndUser(endUser);
                    setOriginalEndUser(endUser);
                }
            },
        }));

        function closeModal() {
            setDialogOpen(false);
            setOpen(false);
            props.onClose(saved, originalEndUser);
        }

        function closeModalCheck(keepOpen: boolean) {
            if (hasChanges.current) {
              setDialogOpen(true);
            } else {
              setDialogOpen(false);
              setOpen(keepOpen);
              props.onClose(saved, originalEndUser);
            }
        }

        function OnEndUserChanged(
            propertyName: string,
            propertyValue: any,
            validationRegex?: RegExp
        ) {
            let proceed = true;
        
            if (validationRegex !== undefined) {
                proceed = validationRegex.test(propertyValue);
            }
        
            if (proceed) {
                let temp: any = { ...endUser };
        
                if (temp[propertyName] != propertyValue)
                {
                    hasChanges.current = true;
                    temp[propertyName] = propertyValue ?? null;
                    setEndUser(temp);
                }
            }
        }

        function CustomerChanged(newCustomer: any) {
            if (newCustomer) {
                let custId = newCustomer.value % 100000;
                setSelectedCustomer(newCustomer);
                let temp = endUser;
                if (temp)
                {
                    temp.customerID = custId;
                    temp.customerName = newCustomer.name;
                    setEndUser(temp);
                }
                hasChanges.current = true;
            }
            else
            {
                setSelectedCustomer(undefined);
            }
        }
                
        function SaveEndUser(close: boolean) {
            if (hasChanges.current)
            {
                if (openMode === OpenMode.EDIT)
                {
                    axios.put<EndUserListItem>('Api/V1/EndUser/', endUser).then(
                        (result) => {
                            showSnackBar("End user saved successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setEndUser(result.data);
                            setOriginalEndUser(result.data);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                } 
                else
                {
                    axios.post<EndUserListItem>('Api/V1/EndUser/', endUser).then(
                        (result) => {
                            showSnackBar("End user created successfully.", "", "success");
                            hasChanges.current = false;
                            setSaved(true);
                            setEndUser(result.data);
                            setOriginalEndUser(result.data);
                            setOpenMode(OpenMode.EDIT);
                            setOpen(!close);
                            setDialogOpen(false);
                            if (close)
                                props.onClose(true, result.data);
                        },
                        (error) => {
                            showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                        }
                    );
                }
            }
        }

        return (
            <div>
                <Modal open={open} onClose={() => closeModalCheck(true)}>
                    <div className={style.ModalMainNarrow}>
                        {!loading ? (
                            <div className={style.ModalBody}>
                                <Title pageTitle="End User" />

                                <div className={style.ModalHeading}>
                                    <Typography variant={ tinyScreen ? "h6" : (smallScreen ? "h5" : "h4") }>End User</Typography>
                                </div>

                                <div className={style.ModalContent}>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Name"
                                            value = {endUser?.endUserName ?? ""}
                                            onChange={(e) => OnEndUserChanged("endUserName", e.target.value.slice(0, 50))}
                                            size="small"
                                        />
                                       <TextField className={style.ModalContentFieldFullWidth}
                                            label="Name (Eng)"
                                            value = {endUser?.endUserEng ?? ""}
                                            onChange={(e) => OnEndUserChanged("endUserEng", e.target.value.slice(0, 70))}
                                            size="small"
                                        />
                                        <Autocomplete className={style.ModalContentFieldFullWidth}
                                            limitTags={2}
                                            onChange={(e, selected) => CustomerChanged(selected)}
                                            options={props.customers}
                                            getOptionLabel={(option: DropDownComboOption) => option.name}
                                            renderOption={(props, option) => {
                                                return (
                                                <li {...props} key={option.value}>
                                                    {option.name}
                                                </li>
                                                );
                                            }}
                                            value={props.customers.find(s => s.value === (selectedCustomer?.value ?? 0).toString()) || null}
                                            style={{ minWidth: tinyScreen ? "244px" : (smallScreen ? "344px" : "464px") }}
                                            renderInput={(params) => (
                                                <TextField
                                                {...params}
                                                variant="outlined"
                                                label={"Customer"}
                                                size="small"
                                                />
                                            )}
                                            size="small"
                                        />
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Region"
                                            value={endUser?.region ?? ""}
                                            onChange={(e) => {
                                                if (e.target.value != "-1")
                                                    OnEndUserChanged("region", e.target.value)
                                            }}
                                            select
                                            size="small"
                                            fullWidth
                                        >
                                            {regionsWithBlank.map((item)=> (
                                                <MenuItem key={item.value} value={item.value}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </div>
                                </div>

                                <div className={style.ModalFooter}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        className={style.margin}
                                        onClick={(e) => {
                                                SaveEndUser(false);
                                            }
                                        }
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Save
                                    </Button>
                                    <div style={{ width: `100%`, minWidth: "10" }}></div>
                                    <Button
                                        variant="contained"
                                        className={style.margin}
                                        onClick={(e) => closeModalCheck(false)}
                                        size="small"
                                        style={{ padding: 1}}
                                    >
                                        Close
                                    </Button>                                
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <CircularProgress color="primary" />
                            </div>
                        )}
                    </div>
                </Modal>

                <SnackBarMessage {...snackSettings} />

                <GeneralDialog
                    title={"Save Changes?"}
                    message={
                        "Changes have been made, did you want to save before closing?"
                    }
                    open={dialogOpen}
                    options={[
                        {
                        text: "Save",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => SaveEndUser(true),
                        color: "secondary",
                        variant: "contained",
                        },
                        {
                        text: "No",
                        action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => closeModal(),
                        color: "primary",
                        variant: "contained",
                        },
                    ]}
                />

            </div>
        );


    }

);


